import React, { useState, useEffect, useCallback } from 'react';
import './JoinAuth.css';
import { FaGoogle } from 'react-icons/fa';
import { FaRegCircleCheck } from 'react-icons/fa6';
import useAuth from '../hooks/useAuth';
import { redirectSignInResult } from '../firebase/config';
import {
  signUpWithEmail,
  signInWithEmail,
  signInWithProvider,
  sendSignInLink,
  isEmailLink,
  storedLinkEmail,
  completeEmailLinkSignIn,
  sendVerificationEmail,
  getMfaResolver,
  startMfaSignIn,
  finishMfaSignIn,
  isPhoneFactor,
  clearRecaptcha,
  signOut,
  friendlyAuthError,
  isRedirectPending,
  clearRedirectPending,
} from '../firebase/auth';
import { trackEvent } from '../firebase/analytics';

const MFA_SIGNIN_RECAPTCHA_ID = 'recaptcha-mfa-signin';

// Live sign-up password rules — keep in sync with the Identity Platform
// password policy (8+ chars, upper, lower, number).
const PASSWORD_RULES = [
  { key: 'length', label: 'At least 8 characters', test: (p) => p.length >= 8 },
  { key: 'upper', label: 'An uppercase letter', test: (p) => /[A-Z]/.test(p) },
  { key: 'lower', label: 'A lowercase letter', test: (p) => /[a-z]/.test(p) },
  { key: 'number', label: 'A number', test: (p) => /[0-9]/.test(p) },
];

const JoinAuth = () => {
  const user = useAuth();

  const [method, setMethod] = useState('password'); // 'password' | 'link'
  const [isSignUp, setIsSignUp] = useState(false);

  // email + password form
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  // passwordless email-link form. `linkSent` is the "check your inbox" state;
  // `completingLink` covers the return trip when the emailed link is opened;
  // `needsLinkEmail` is the cross-device case where we can't recall the address.
  const [linkEmail, setLinkEmail] = useState('');
  const [linkSent, setLinkSent] = useState(false);
  const [completingLink, setCompletingLink] = useState(() => isEmailLink());
  const [needsLinkEmail, setNeedsLinkEmail] = useState(false);

  // Returning from a social (Google) redirect sign-in. Seeded synchronously so
  // the loading indicator paints on the first render, before getRedirectResult
  // resolves — otherwise the login form flashes for that async gap.
  const [completingRedirect, setCompletingRedirect] = useState(() => isRedirectPending());

  // MFA sign-in challenge (second factor)
  const [mfaResolver, setMfaResolver] = useState(null);
  const [mfaVerificationId, setMfaVerificationId] = useState(null);
  const [mfaCode, setMfaCode] = useState('');

  const [busy, setBusy] = useState('');
  const [error, setError] = useState('');

  const resetMessages = () => setError('');

  // Runs an auth action; if it needs a second factor, transitions into the
  // MFA challenge instead of surfacing an error.
  const attempt = async (label, fn) => {
    resetMessages();
    setBusy(label);
    try {
      await fn();
      trackEvent('auth_success', { method: label, timestamp: new Date().toISOString() });
    } catch (err) {
      if (err?.code === 'auth/multi-factor-auth-required') {
        await beginMfaChallenge(err, label);
      } else {
        setError(friendlyAuthError(err));
        trackEvent('auth_error', { method: label, code: err?.code || 'unknown' });
      }
    } finally {
      setBusy('');
    }
  };

  const beginMfaChallenge = useCallback(async (err, label) => {
    try {
      const resolver = getMfaResolver(err);
      const idx = resolver.hints.findIndex((h) => isPhoneFactor(h.factorId));
      if (idx < 0) {
        setError('This account requires an unsupported second factor.');
        return;
      }
      const verificationId = await startMfaSignIn(resolver, idx, MFA_SIGNIN_RECAPTCHA_ID);
      setMfaResolver(resolver);
      setMfaVerificationId(verificationId);
      trackEvent('mfa_challenge_sent', { method: label });
    } catch (e) {
      setError(friendlyAuthError(e));
    }
  }, []);

  // The deployed Google flow signs in via a full-page redirect, so an
  // MFA-enrolled account surfaces auth/multi-factor-auth-required from
  // getRedirectResult once the page reloads — never from the click handler.
  // Pick that up here and start the SMS challenge just like the inline paths.
  useEffect(() => {
    let active = true;
    redirectSignInResult.then(({ user: redirectUser, error: redirectError }) => {
      clearRedirectPending();
      if (!active) return;
      if (redirectError) {
        // The redirect finished but sign-in didn't complete: drop the loading
        // indicator and surface the challenge (MFA) or the error.
        setCompletingRedirect(false);
        if (redirectError.code === 'auth/multi-factor-auth-required') {
          beginMfaChallenge(redirectError, 'google');
        } else {
          setError(friendlyAuthError(redirectError));
        }
      } else if (!redirectUser) {
        // No redirect was actually pending (a normal page load) — show the form.
        setCompletingRedirect(false);
      }
      // Happy path (redirectUser present): keep the indicator up; the auth
      // listener sets `user` and LoginPage navigates away, so there's nothing
      // to fall back to.
    });
    return () => {
      active = false;
    };
  }, [beginMfaChallenge]);

  // Returning from an emailed sign-in link: finish the sign-in as soon as we
  // mount. The address is normally remembered from when the link was requested;
  // if the link was opened on another device we have to ask for it (Firebase
  // requires it, to stop a leaked link alone from being enough to sign in).
  useEffect(() => {
    if (!isEmailLink()) return undefined;
    const remembered = storedLinkEmail();
    if (!remembered) {
      setNeedsLinkEmail(true);
      setCompletingLink(false);
      return undefined;
    }
    let active = true;
    (async () => {
      try {
        await completeEmailLinkSignIn(remembered);
        trackEvent('auth_success', {
          method: 'email_link',
          timestamp: new Date().toISOString(),
        });
      } catch (err) {
        if (!active) return;
        if (err?.code === 'auth/multi-factor-auth-required') {
          await beginMfaChallenge(err, 'email_link');
        } else {
          setError(friendlyAuthError(err));
          trackEvent('auth_error', { method: 'email_link', code: err?.code || 'unknown' });
        }
      } finally {
        if (active) setCompletingLink(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [beginMfaChallenge]);

  const handleEmailSubmit = (e) => {
    e.preventDefault();
    attempt(isSignUp ? 'email_signup' : 'email_signin', async () => {
      if (isSignUp) {
        const newUser = await signUpWithEmail(name.trim(), email.trim(), password);
        // Kick off email verification so they can later enable two-step.
        try {
          await sendVerificationEmail(newUser);
        } catch (_) {
          /* non-fatal */
        }
      } else {
        await signInWithEmail(email.trim(), password);
      }
    });
  };

  const handleSocial = (providerKey) => {
    attempt(providerKey, () => signInWithProvider(providerKey));
  };

  const handleSendLink = (e) => {
    e.preventDefault();
    attempt('email_link_send', async () => {
      await sendSignInLink(linkEmail.trim());
      setLinkSent(true);
    });
  };

  // Cross-device return: the address couldn't be recalled, so the visitor
  // re-enters it to complete the link they just opened.
  const handleConfirmLinkEmail = (e) => {
    e.preventDefault();
    attempt('email_link_complete', async () => {
      await completeEmailLinkSignIn(linkEmail.trim());
      setNeedsLinkEmail(false);
    });
  };

  const handleMfaVerify = (e) => {
    e.preventDefault();
    attempt('mfa_verify', async () => {
      await finishMfaSignIn(mfaResolver, mfaVerificationId, mfaCode.trim());
      cancelMfaChallenge();
    });
  };

  const cancelMfaChallenge = () => {
    setMfaResolver(null);
    setMfaVerificationId(null);
    setMfaCode('');
    clearRecaptcha(MFA_SIGNIN_RECAPTCHA_ID);
  };

  const handleSignOut = () => {
    resetMessages();
    signOut();
    setLinkSent(false);
    cancelMfaChallenge();
  };

  /* ---------- Signed-in state (fallback; LoginPage redirects before this) ---------- */
  if (user) {
    const who = user.displayName || user.email || user.phoneNumber || 'friend';
    return (
      <div className="auth-panel auth-welcome">
        <FaRegCircleCheck className="auth-welcome-icon" />
        <h3>Welcome, {who}!</h3>
        <p>You’re now part of the Dutchess County Bhabani family.</p>
        <button className="btn-primary" onClick={handleSignOut}>
          Sign out
        </button>
      </div>
    );
  }

  /* ---------- Returning from an emailed sign-in link ---------- */
  if (completingLink) {
    return (
      <div className="auth-panel auth-welcome">
        <h3>Signing you in…</h3>
        <p>One moment while we finish signing you in from your email link.</p>
      </div>
    );
  }

  /* ---------- Returning from a social (Google) redirect ---------- */
  if (completingRedirect) {
    return (
      <div className="auth-panel auth-welcome">
        <span className="auth-spinner" aria-hidden="true" />
        <h3>Signing you in…</h3>
        <p>One moment while we finish signing you in with Google.</p>
      </div>
    );
  }

  /* ---------- Link opened on a different device: confirm the address ---------- */
  if (needsLinkEmail) {
    return (
      <div className="auth-panel">
        <div className="mfa-head">
          <h3>Confirm your email</h3>
          <p>
            It looks like you opened this link on a different device. Enter the
            address you requested it for and we’ll finish signing you in.
          </p>
        </div>
        <form className="auth-form" onSubmit={handleConfirmLinkEmail}>
          <input
            type="email"
            placeholder="Email address"
            aria-label="Email address"
            value={linkEmail}
            onChange={(e) => setLinkEmail(e.target.value)}
            required
          />
          <button className="btn-primary" type="submit" disabled={!!busy}>
            {busy === 'email_link_complete' ? 'Signing in…' : 'Sign in'}
          </button>
        </form>
        {error && <p className="auth-error">{error}</p>}
      </div>
    );
  }

  /* ---------- MFA sign-in challenge ---------- */
  if (mfaResolver) {
    const hint = mfaResolver.hints.find((h) => isPhoneFactor(h.factorId));
    return (
      <div className="auth-panel">
        <div className="mfa-head">
          <h3>Two-step verification</h3>
          <p>
            Enter the code we texted to{' '}
            <strong>{hint?.phoneNumber || 'your phone'}</strong>.
          </p>
        </div>
        <form className="auth-form" onSubmit={handleMfaVerify}>
          <input
            type="text"
            inputMode="numeric"
            placeholder="6-digit code"
            aria-label="Verification code"
            value={mfaCode}
            onChange={(e) => setMfaCode(e.target.value)}
            required
          />
          <button className="btn-primary" type="submit" disabled={!!busy}>
            {busy === 'mfa_verify' ? 'Verifying…' : 'Verify & sign in'}
          </button>
          <p className="auth-switch">
            <button type="button" onClick={cancelMfaChallenge}>
              Cancel
            </button>
          </p>
        </form>
        {error && <p className="auth-error">{error}</p>}
        <div id={MFA_SIGNIN_RECAPTCHA_ID} />
      </div>
    );
  }

  /* ---------- Auth forms ---------- */
  return (
    <div className="auth-panel">
      <div className="auth-tabs">
        <button
          className={method === 'password' ? 'active' : ''}
          onClick={() => {
            setMethod('password');
            resetMessages();
          }}
          type="button"
        >
          Password
        </button>
        <button
          className={method === 'link' ? 'active' : ''}
          onClick={() => {
            setMethod('link');
            resetMessages();
          }}
          type="button"
        >
          Email link
        </button>
      </div>

      {method === 'password' && (
        <form className="auth-form" onSubmit={handleEmailSubmit}>
          {isSignUp && (
            <input
              type="text"
              placeholder="Your name"
              aria-label="Your name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          )}
          <input
            type="email"
            placeholder="Email address"
            aria-label="Email address"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
          />
          <input
            type="password"
            placeholder="Password"
            aria-label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            minLength={isSignUp ? 8 : undefined}
            aria-describedby={isSignUp ? 'password-reqs' : undefined}
            required
          />
          {isSignUp && (
            <ul id="password-reqs" className="pw-checklist" aria-label="Password requirements">
              {PASSWORD_RULES.map((rule) => {
                const met = rule.test(password);
                return (
                  <li key={rule.key} className={met ? 'met' : ''}>
                    <span className="pw-check" aria-hidden="true">{met ? '✓' : '○'}</span>
                    {rule.label}
                    <span className="sr-only">{met ? ' — met' : ' — not met'}</span>
                  </li>
                );
              })}
            </ul>
          )}
          <button className="btn-primary" type="submit" disabled={!!busy}>
            {busy === 'email_signup' || busy === 'email_signin'
              ? 'Please wait…'
              : isSignUp
              ? 'Create account'
              : 'Sign in'}
          </button>
          <p className="auth-switch">
            {isSignUp ? 'Already a member?' : 'New here?'}{' '}
            <button
              type="button"
              onClick={() => {
                setIsSignUp(!isSignUp);
                resetMessages();
              }}
            >
              {isSignUp ? 'Sign in' : 'Create an account'}
            </button>
          </p>
        </form>
      )}

      {method === 'link' && (
        <form className="auth-form" onSubmit={handleSendLink}>
          {!linkSent ? (
            <>
              <p className="auth-hint">
                No password needed — we’ll email you a link that signs you in.
                It works for a new account too.
              </p>
              <input
                type="email"
                placeholder="Email address"
                aria-label="Email address"
                value={linkEmail}
                onChange={(e) => setLinkEmail(e.target.value)}
                required
              />
              <button className="btn-primary" type="submit" disabled={!!busy}>
                {busy === 'email_link_send' ? 'Sending…' : 'Email me a sign-in link'}
              </button>
            </>
          ) : (
            <>
              <p className="auth-sent">
                Check your inbox — we sent a sign-in link to{' '}
                <strong>{linkEmail.trim()}</strong>. Open it on this device to
                finish signing in.
              </p>
              <p className="auth-switch">
                <button
                  type="button"
                  onClick={() => {
                    setLinkSent(false);
                    resetMessages();
                  }}
                >
                  Use a different address
                </button>
              </p>
            </>
          )}
        </form>
      )}

      <div className="auth-divider"><span>or continue with</span></div>

      <div className="social-auth">
        <button
          type="button"
          className="social-btn"
          onClick={() => handleSocial('google')}
          disabled={!!busy}
          aria-label="Continue with Google"
        >
          <FaGoogle /> Continue with Google
        </button>
      </div>

      {error && <p className="auth-error">{error}</p>}

      {/* Invisible reCAPTCHA mount point (MFA challenge) */}
      <div id={MFA_SIGNIN_RECAPTCHA_ID} />
    </div>
  );
};

export default JoinAuth;
